import { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { useLazyGetBranchByCodeQuery } from '@/features/local-configuration/api/local-configuration-api';
import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';

import {
  selectBranchCode,
  selectBranchDetail,
  selectBranches,
  setActiveBranch,
  setBranchDetail,
} from '../store/auth-slice';
import type { Pro20BranchSummary } from '../types/auth.types';

/**
 * Active branch context — use this hook anywhere you need the current branch.
 * `branchDetail` is the full object from GET /branches/{branchCode}.
 */
export function useActiveBranch() {
  const dispatch = useAppDispatch();
  const branchCode = useAppSelector(selectBranchCode);
  const branchDetail = useAppSelector(selectBranchDetail);
  const branches = useAppSelector(selectBranches);
  const [getBranch, { isFetching }] = useLazyGetBranchByCodeQuery();
  const [isSwitching, setIsSwitching] = useState(false);

  const activeBranch = useMemo((): Pro20BranchSummary | null => {
    if (!branchCode) return null;
    return branches.find((b) => b.branchCode === branchCode) ?? null;
  }, [branchCode, branches]);

  const branchName =
    branchDetail?.branchName ?? activeBranch?.branchName ?? null;

  const switchBranch = useCallback(
    async (nextBranchCode: string) => {
      if (!nextBranchCode || nextBranchCode === branchCode) {
        return;
      }

      setIsSwitching(true);
      dispatch(setActiveBranch(nextBranchCode));

      try {
        const detail = await getBranch(nextBranchCode).unwrap();
        dispatch(setBranchDetail(detail));

        dispatch(
          baseApi.util.invalidateTags([
            API_TAGS.Dashboard,
            API_TAGS.Users,
            API_TAGS.Notifications,
            API_TAGS.Settings,
            API_TAGS.Reports,
            API_TAGS.Bookings,
            API_TAGS.GiftCards,
            API_TAGS.Messages,
            { type: API_TAGS.Branch, id: nextBranchCode },
          ]),
        );
      } catch {
        dispatch(setBranchDetail(null));
      } finally {
        setIsSwitching(false);
      }
    },
    [branchCode, dispatch, getBranch],
  );

  return {
    branchCode,
    branchDetail,
    branches,
    activeBranch,
    branchName,
    switchBranch,
    isSwitchingBranch: isSwitching || isFetching,
  };
}
